/**
 * De lessenlijst: banner, lessen, pauzes en de NU-markering — met links de
 * tijdlijn die laat zien hoe ver de dag is.
 *
 * De plaatsingslogica zelf staat in lib/roosterItems.ts; dit component tekent
 * alleen wat daar uit komt. Die scheiding is er zodat de randgevallen (pauze na het
 * laatste uur, "nu" ná de laatste les, welke les er nú loopt) getest kunnen worden
 * zonder DOM.
 *
 * De lessen verdelen de hoogte die er is in plaats van hun eigen hoogte te bepalen:
 * een schooldag hoort op een wandtablet in één beeld te passen, zonder te scrollen.
 */
import { useLayoutEffect, useRef } from 'react';
import type { Rooster } from '../types';
import { bouwRoosterItems, dagVoortgang } from '../lib/roosterItems';
import { minutenVan } from '../lib/tijd';
import { Dagbanner } from './Dagbanner';
import { LesKaart } from './LesKaart';
import { NuLijn } from './NuLijn';
import { PauzeRegel } from './PauzeRegel';

interface Props {
  rooster: Rooster | null;
  tijd: Date;
  /** Alleen op de dag van vandaag; op een volgende dag zegt "nu" niets. */
  toonNu: boolean;
}

export function Roosterlijst({ rooster, tijd, toonNu }: Props) {
  const lijst = useRef<HTMLDivElement>(null);
  const minuut = Math.floor(tijd.getTime() / 60_000);
  useLayoutEffect(() => {
    const element = lijst.current;
    if (!element) return;
    // Measure real wrapped text, including notes, after fonts load and on resize.
    // Shrink typography and spacing together; never conceal overflowing text.
    const pasAan = () => {
      let schaal = 1;
      element.style.setProperty('--rooster-schaal', '1');
      while (element.scrollHeight > element.clientHeight + 1 && schaal > 0.2) {
        schaal -= 0.025;
        element.style.setProperty('--rooster-schaal', String(schaal));
      }
    };
    pasAan();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(pasAan);
    observer?.observe(element);
    let actief = true;
    void document.fonts?.ready.then(() => { if (actief) pasAan(); });
    return () => { actief = false; observer?.disconnect(); };
  }, [rooster, minuut, toonNu]);

  if (!rooster) {
    return (
      <main>
        <div className="tijdlijn" />
        <div className="lesson-list" ref={lijst}>
          <div className="empty-state">Rooster wordt geladen…</div>
        </div>
      </main>
    );
  }

  const nuMinuten = toonNu ? minutenVan(tijd) : null;
  const items = bouwRoosterItems(rooster.lessen, rooster.pauzes, nuMinuten);
  const voortgang = dagVoortgang(rooster.lessen, nuMinuten);
  // Om en om wit en lichtblauw, geteld over de lessen alleen: een pauzeregel of de
  // NU-lijn ertussen breekt de afwisseling niet.
  let lesNummer = 0;

  return (
    <main>
      {/* De tijdlijn staat als eigen kolom naast de lessen en niet als rand op de
          kaarten: zo loopt hij ook dóór langs de pauzes, en dat is precies waar
          "hoe ver is de dag" het vaakst wordt afgelezen.

          Hij verschijnt alleen als er iets te volgen valt. Op een lege dag, en op
          de volgende schooldag, levert `dagVoortgang` niets op — en dan zou een
          balkje naast een leeg rooster of naast "morgen" alleen maar suggereren dat
          er ergens een moment in zit. */}
      <div className="tijdlijn" aria-hidden="true">
        {voortgang !== null && (
          <div className="track">
            <div className="fill" style={{ height: `${voortgang * 100}%` }} />
          </div>
        )}
      </div>

      <div className="lesson-list" ref={lijst}>
        {rooster.banner && <Dagbanner banner={rooster.banner} />}

        {rooster.lessen.length === 0 ? (
          // Een lege dag is geen fout: een leerling kan gewoon vrij zijn. De
          // banner hierboven blijft staan, want een projectdag mét nul lessen is
          // precies het geval waarin dat onderscheid uitmaakt.
          <div className="empty-state">Geen lessen vandaag</div>
        ) : (
          items.map((item) => {
            switch (item.soort) {
              case 'pauze':
                return <PauzeRegel key={item.sleutel} pauze={item.pauze} />;
              case 'nu':
                return <NuLijn key={item.sleutel} />;
              case 'les':
                return (
                  <LesKaart
                    key={item.sleutel}
                    les={item.les}
                    nu={item.nu}
                    zebra={lesNummer++ % 2 === 1 && item.les.status === 'normal'}
                  />
                );
            }
          })
        )}
      </div>
    </main>
  );
}
